"use client";

import { LegalPageLayout } from "@/components/legal/legal-page-layout";
import { useTranslations } from "@/hooks/use-translations";

export default function TermsPage() {
  const { t } = useTranslations("legal");
  
  return (
    <LegalPageLayout title={t("termsOfServiceLabel")} lastUpdated="18 August 2026">
      <div className="prose prose-neutral max-w-none space-y-6 text-neutral-700">
          <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-4 mb-6">
            <p className="text-sm text-amber-900">
              <strong>{t("governingLanguage")}</strong>
            </p>
          </div>
          
          <p>
            These Terms of Service ("Terms") govern your access to and use of the GrowthSync app, website, and related services ("the Platform"), operated by GrowthSync Network Ltd ("we", "our", "us").
          </p>
          <p>
            By creating an account or using the Platform, you agree to be bound by these Terms.
          </p>

          <section>
            <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">1. Overview</h2>
            <p>
              GrowthSync provides a digital networking and event management platform for professionals, founders, and businesses. Users can connect, message, join or host events, and participate in community spaces.
            </p>
            <p>
              We reserve the right to modify or update these Terms at any time. Updated versions will be posted on our website.
            </p>
          </section>

          <section>
            <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">2. Eligibility</h2>
            <p>
              You must be at least 18 years old to use GrowthSync. By registering, you confirm that all information you provide is accurate and complete.
            </p>
          </section>

          <section>
            <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">3. Account and user responsibilities</h2>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>You are responsible for maintaining the confidentiality of your login credentials.</li>
              <li>You are responsible for all activity that occurs under your account.</li>
              <li>You must provide accurate and complete information when creating your account.</li>
              <li>You must be at least 18 years old to use the Platform.</li>
              <li>You must comply with all applicable laws while using the Platform.</li>
              <li>We reserve the right to suspend or terminate accounts that violate these Terms.</li>
            </ul>
          </section>

          <section>
            <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">3a. Prohibited activities</h2>
            <p>
              You agree not to use the Platform to:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>Post, share, or transmit content that is unlawful, harmful, threatening, abusive, harassing, defamatory, or discriminatory.</li>
              <li>Impersonate any person or entity, or falsely represent your affiliation with any person or entity.</li>
              <li>Engage in spamming, phishing, or other fraudulent activities.</li>
              <li>Post spam, promotional content, or unsolicited commercial messages.</li>
              <li>Share content that violates intellectual property rights of others.</li>
              <li>Upload or transmit viruses, malware, or other harmful code.</li>
              <li>Interfere with or disrupt the Platform's security, servers, or networks.</li>
              <li>Collect or harvest information about other users without their consent.</li>
              <li>Use automated systems (bots, scrapers) to access the Platform without permission.</li>
              <li>Engage in any activity that could harm, disable, or impair the Platform or other users.</li>
            </ul>
            <p>
              We use automated content moderation systems to detect and prevent prohibited content. Violations may result in content removal, account suspension, or permanent ban.
            </p>
            <p className="mt-4">
              <strong>Child Safety:</strong> GrowthSync has a zero tolerance policy for child sexual abuse and exploitation material (CSAM/CSAE). 
              For detailed information about our child safety standards, prohibited content, and reporting procedures, please see our{" "}
              <a href="/child-safety" className="text-blue-600 hover:text-blue-700 hover:underline">Child Safety Standards (CSAE) page</a>.
            </p>
          </section>

          <section>
            <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">3b. Reporting, blocking, and content moderation</h2>
            <p>
              GrowthSync provides the following tools to help keep the platform safe for all users:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>
                <strong>Reporting:</strong> You can report any user, post, event, opportunity, community, or
                other content directly within the app. All reports are reviewed by our moderation team within
                24 hours of submission.
              </li>
              <li>
                <strong>Blocking:</strong> You can block any other user at any time via their profile or the
                block/report menu. Blocked users are removed from your experience across the entire platform
                immediately. You can view and manage your blocked users list in Settings.
              </li>
              <li>
                <strong>Automated moderation:</strong> We use automated content filters and profanity
                detection, which look for known words and patterns, to detect and act on prohibited content
                before it reaches other users.
              </li>
              <li>
                <strong>Enforcement:</strong> Violations of these Terms may result in content removal, a
                warning, account suspension, or permanent ban, at our sole discretion. We will notify you
                where reasonably practicable.
              </li>
            </ul>
            <p className="mt-4">
              To report a serious safety concern or contact our moderation team directly, email{" "}
              <a href="mailto:contact@growth-sync.com" className="text-blue-600 hover:text-blue-700 hover:underline">
                contact@growth-sync.com
              </a>.
            </p>
          </section>

          <section>
            <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">4. Payments and subscriptions</h2>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>
                <strong>iOS (App Store):</strong> Subscription purchases made within the iOS app are processed
                exclusively through Apple In-App Purchase (IAP). These transactions are governed by Apple's
                terms and conditions. You can manage and cancel iOS subscriptions at any time in your
                Apple ID settings (Settings → [your name] → Subscriptions).
              </li>
              <li>
                <strong>Web and Android:</strong> Subscription purchases on our website and Android app are
                processed securely via Stripe. By purchasing, you authorise recurring payments until you cancel.
              </li>
              <li>Subscription fees are charged in advance on a recurring basis (monthly or yearly) until cancelled.</li>
              <li>Cancellation takes effect at the end of the current billing period. Access to paid features continues until that date.</li>
              <li>Refunds are subject to the refund policy of the platform through which you purchased (Apple or Stripe) and applicable law.</li>
              <li>All pricing is displayed inclusive of applicable taxes where required.</li>
              <li>Referral rewards, ambassador commissions, or promotional incentives offered within the Platform may be modified or discontinued at any time without prior notice.</li>
              <li>Ambassador and referral programs are subject to separate terms and conditions. Commission rates, payment schedules, and eligibility criteria may change at our discretion.</li>
            </ul>
          </section>

          <section>
            <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">5. Events and third-party content</h2>
            <p>
              Users and event hosts are responsible for their own event listings and content. GrowthSync provides the platform but does not guarantee event quality, accuracy, or outcomes.
            </p>
            <p>
              We may remove any content or events that breach our policies or legal standards.
            </p>
            <p>
              Users are solely responsible for the content they create, post, or share. GrowthSync does not endorse or verify user-generated content.
            </p>
          </section>

          <section>
            <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">6. Data use and privacy</h2>
            <p>
              Your use of GrowthSync is also governed by our <a href="/privacy" className="text-blue-600 hover:text-blue-700 hover:underline">Privacy Policy</a>.
            </p>
            <p>
              By using the app, you consent to the collection and processing of your data as described there, including any optional third-party integrations you choose to connect.
            </p>
            <p className="mt-4">
              GrowthSync does not use artificial intelligence, and does not send your data to any
              third-party artificial intelligence provider. Features that once did, including the in-app
              assistant, profile rewriting and content suggestions, have been removed. The things the
              Platform still works out for you, such as your profile score, match scores and safety checks
              on what members post, run on our own servers using only information you have already given
              us. Section 12 of the <a href="/privacy" className="text-blue-600 hover:text-blue-700 hover:underline">Privacy Policy</a> sets this out in full.
            </p>
          </section>

          <section>
            <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">7. Intellectual property</h2>
            <p>
              All GrowthSync branding, software, and design are the property of GrowthSync Network Ltd.
            </p>
            <p>
              You may not copy, modify, or redistribute any part of the Platform without written permission.
            </p>
            <p>
              You retain ownership of the content you post, but grant us a non-exclusive licence to display and distribute it within the app.
            </p>
          </section>

          <section>
            <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">8. Termination</h2>
            <p>
              We may suspend or permanently disable your account if you breach these Terms, misuse the Platform, or engage in unlawful activity.
            </p>
            <p>
              You may delete your account at any time by contacting support or using in-app settings. For detailed instructions on how to delete your account and what data will be removed, please see our <a href="/account-deletion" className="text-blue-600 hover:text-blue-700 hover:underline">Account Deletion page</a>. If you wish to delete only specific data (such as messages, posts, connections, or profile information) without deleting your entire account, you can do so directly in the app. See our <a href="/data-deletion" className="text-blue-600 hover:text-blue-700 hover:underline">Data Deletion page</a> for instructions.
            </p>
          </section>

          <section>
            <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">9. Limitation of liability</h2>
            <p>
              GrowthSync is provided "as is" and "as available."
            </p>
            <p>
              We do not guarantee uninterrupted or error-free service.
            </p>
            <p>
              To the extent permitted by law, GrowthSync Network Ltd is not liable for any indirect, incidental, or consequential damages arising from your use of the Platform, including but not limited to loss of data, loss of profits, or business interruption.
            </p>
            <p>
              Our total liability to you for any claims arising from these Terms or your use of the Platform shall not exceed the amount you paid to us in the 12 months preceding the claim.
            </p>
          </section>

          <section>
            <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">9a. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless GrowthSync Network Ltd, its officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses (including legal fees) arising from:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>Your use of the Platform.</li>
              <li>Your violation of these Terms.</li>
              <li>Your violation of any rights of another user or third party.</li>
              <li>Content you post, share, or transmit through the Platform.</li>
            </ul>
          </section>

          <section>
            <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">10. Dispute resolution and complaints</h2>
            <p>
              If you have a complaint about the Platform or our services, please contact us at <a href="mailto:contact@growth-sync.com" className="text-blue-600 hover:text-blue-700 hover:underline">contact@growth-sync.com</a>. We will endeavour to resolve your complaint promptly and fairly.
            </p>
            <p>
              If we are unable to resolve your complaint, you may seek resolution through alternative dispute resolution services or the courts, as applicable.
            </p>
          </section>

          <section>
            <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">11. Force majeure</h2>
            <p>
              We shall not be liable for any failure to perform our obligations under these Terms due to circumstances beyond our reasonable control, including but not limited to natural disasters, war, terrorism, pandemics, internet failures, or government actions.
            </p>
          </section>

          <section>
            <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">12. Severability</h2>
            <p>
              If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall remain in full force and effect. The invalid provision shall be replaced with a valid provision that most closely reflects the intent of the original provision.
            </p>
          </section>

          <section>
            <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">13. Entire agreement</h2>
            <p>
              These Terms, together with our <a href="/privacy" className="text-blue-600 hover:text-blue-700 hover:underline">Privacy Policy</a> and <a href="/cookies" className="text-blue-600 hover:text-blue-700 hover:underline">Cookie Policy</a>, constitute the entire agreement between you and GrowthSync Network Ltd regarding your use of the Platform, and supersede all prior agreements and understandings.
            </p>
          </section>

          <section>
            <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">14. Assignment</h2>
            <p>
              We may assign or transfer these Terms, or any rights or obligations hereunder, to any third party without your consent. You may not assign or transfer these Terms without our prior written consent.
            </p>
          </section>

          <section>
            <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">15. Governing law</h2>
            <p>
              These Terms are governed by and interpreted in accordance with the laws of England and Wales.
            </p>
            <p>
              Any disputes shall be subject to the exclusive jurisdiction of the courts of England and Wales.
            </p>
          </section>

          <section>
            <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">16. Contact information</h2>
            <p>
              For all user or legal enquiries, please contact:
            </p>
            <p className="mt-2">
              GrowthSync Network Ltd<br />
              Email: <a href="mailto:contact@growth-sync.com" className="text-blue-600 hover:text-blue-700 hover:underline">contact@growth-sync.com</a><br />
              Registered Office: Unit 6, Balfour Court, Leyland, PR25 2TF<br />
              Company No. 16795645
            </p>
          </section>
        </div>
    </LegalPageLayout>
  );
}
